import { collection, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { ResponsibleSchema, type Responsible } from '@etiquetachef/types';
import { firestore } from './firebase';
import { snapshotData } from './firestoreNormalize';

const TAG = 'admin/firestoreResponsibles';

/** Estado da aba Responsáveis do detalhe do tenant. */
export type ResponsibleListResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; responsibles: Responsible[] }
  | { kind: 'ERROR'; error: unknown };

/**
 * Lista os responsáveis do tenant. Ordena `isDefault` no topo, depois nome —
 * mesma semântica de `subscribeResponsibles` de `apps/app`. Docs inválidos no
 * parse Zod ficam logados e ignorados.
 *
 * Origem: sem issue — commit f948c64e4, 2026-06-10 (abas Usuários e
 * Responsáveis na página do tenant).
 *
 * Contrato: listener em `tenants/{tenantId}/responsibles`; erro de snapshot vira `ERROR` (a tela mostra o alerta).
 *
 * Usado por: `features/responsibles/useResponsibles.ts`.
 *
 * Arquitetura: rule `match /responsibles/{responsibleId}` sob
 * `match /tenants/{tenantId}`; escrita pelas callables de responsáveis.
 *
 * Dados: `tenants/{tid}/responsibles/{id}` — schema `ResponsibleSchema`
 * (`name`, `isDefault`, …).
 *
 * Falhas conhecidas:
 *  - Responsável fora do schema some só com `console.warn`, sem report (#926).
 */
export function subscribeResponsibles(
  tenantId: string,
  onResult: (r: ResponsibleListResult) => void,
): Unsubscribe {
  console.info(`${TAG}: subscribe tenant=${tenantId}`);
  const ref = collection(firestore, 'tenants', tenantId, 'responsibles');
  return onSnapshot(
    ref,
    (snap) => {
      const responsibles: Responsible[] = [];
      for (const d of snap.docs) {
        const parsed = ResponsibleSchema.safeParse({ id: d.id, ...snapshotData(d) });
        if (parsed.success) {
          responsibles.push(parsed.data);
        } else {
          console.warn(`${TAG}: responsible ${d.id} ignorado`, parsed.error.issues);
        }
      }
      responsibles.sort((a, b) => {
        if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
      onResult({ kind: 'READY', responsibles });
    },
    (err) => {
      console.error(`${TAG}: snapshot error`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}
